import React from 'react';

import { Redirect, Route, RouteProps, Switch } from 'react-router-dom';
import { customerCommunicationFeatureEnabledForCountries } from './config/Api.config';
import { useUACAccess, useFeatureCountryAccess } from './CustomHook/useRouteAccess';

import AppConstants from './constants';
import UACConstants, { UAC_ROUTE_MAP } from './constants/UACConstants';

import NotFound from './common/NotFoundPage';
import UserLogin from './Login';
import RoutePlanning from './RoutePlanning';
import CreateRoute from './RoutePlanning/CreateRoute';
import ReconciliationDashboard from "./Dashboard/ReconciliationDashboard";
import PerformanceDashboard from "./Dashboard/PerformanceDashboard";
import HubWiseDashboard from "./Dashboard/HubwiseDashboard";
import Users from './Users';
import DelveOrderList from './Delve/OrderList';
import RouteTracking from './RouteTracking';
import ValetTrolley from './ValetTrolley';
import Hubs from 'Hubs';
import ClickAndCollect from 'ClickAndCollect';
import CarrefourDelivery from './CarrefourDelivery';
import AccessDenied from './common/AccessDenied';
import Express from './Express';
import Drivers from './DriverManagement';
import CODPortal from './CODPortal/express';
import StdCodPortal from './CODPortal/standard';
import DelveDashboard from './Delve/Dashboard';
import OpsDashboard from './OpsDashboard';
import QRCard from './DownloadApp';
import OrderList from 'orderList';
import StagingArea from './StagingArea';
import CustomerCommunication from './CustomerCommunication';
import StoreManagementPage from './StoreManagement/StoreManagementPage';
import DriverHelperTip from 'DriverHelperTip';
import DriverLocations from 'DriverLocations';
import OnBoardingPage from './OnBoarding';
import ReturnsList from './ReturnList';
import FeaturesConstants from 'constants/FeaturesContants';
import Reports from './Reports';
import DownloadApp from './DownloadApp';

interface RoutesProps {
  isLoggedIn: boolean;
  role: any;
  countryCode: any;
  features: string[];
  uacPermissions?: Array<{
    key: string;
    permissions: string[];
    type: string;
    parentKey?: string;
  }>;
  defaultMenu?: string;
}

interface GuardedRouteProps extends RouteProps {
  routeGuards?: RouteGuards;
  extraProps?: any;
  isCHDManager?: any;
  role: any;
  uacKey?: string;
  uacPermission?: 'VIEW' | 'EDIT';
  uacPermissions?: Array<{
    key: string;
    permissions: string[];
    type: string;
    parentKey?: string;
  }>;
  featuredCountry?: string; // Add feature key for country check
  countryCode?: string; // Add country code
  defaultMenu?: string; // Add defaultMenu
}

interface RouteGuards {
  [key: string]: boolean;
}

const RouteGuards = (guards: RouteGuards | undefined) => {
  let isActive = false;
  if (guards) {
    isActive = true;
    for (const key in guards) {
      if (!guards[key]) {
        isActive = false;
        break;
      }
    }
  }
  return isActive;
};

// Updated: Use defaultMenu from UAC data for redirection
const checkAuthRedirection = (
  role: any,
  uacPermissions: Array<{
    key: string;
    permissions: string[];
    type: string;
    parentKey?: string;
  }> = [],
  defaultMenu?: string
): string => {
  // First priority: Use defaultMenu if provided
  if (defaultMenu && UAC_ROUTE_MAP[defaultMenu]) {
    return UAC_ROUTE_MAP[defaultMenu];
  }

  // Second priority: Find first authorized route from UAC permissions
  for (const uacItem of uacPermissions) {
    if (uacItem.permissions.includes('VIEW') && UAC_ROUTE_MAP[uacItem.key]) {
      return UAC_ROUTE_MAP[uacItem.key];
    }
  }

  // Default fallback
  return '/';
};

const AuthenticatedRoute: React.FC<GuardedRouteProps> = ({
  component: Component,
  routeGuards,
  extraProps,
  role,
  uacKey,
  uacPermission = 'VIEW',
  uacPermissions = [],
  featuredCountry,
  countryCode,
  defaultMenu,
  ...rest
}) => {
  // Call all hooks at the top before any conditional returns
  const hasUACAccess = useUACAccess(uacKey, uacPermission, uacPermissions);
  const hasFeatureAccess = useFeatureCountryAccess(featuredCountry, countryCode);
  const isRouteGuardPassed = React.useMemo(
    () => RouteGuards(routeGuards),
    [routeGuards]
  );

  // Calculate default route using defaultMenu or UAC permissions
  const defaultRoute = React.useMemo(() => {
    return checkAuthRedirection(role, uacPermissions, defaultMenu);
  }, [role, uacPermissions, defaultMenu]);

  // Route render logic with proper authentication flow
  const renderRoute = React.useCallback(
    (routeProps: any) => {
      // Check if route guards pass
      if (!isRouteGuardPassed) {
        return <Redirect to={{ pathname: AppConstants.ROUTES.LOGIN }} />;
      }

      // Render component or redirect to default authorized page
      if (Component) {
        return <Component {...routeProps} {...extraProps} />;
      }

      // Redirect to default route when no component specified
      return <Redirect to={{ pathname: defaultRoute }} />;
    },
    [Component, extraProps, isRouteGuardPassed, defaultRoute]
  );

  // Unauthenticated users should be redirected to login, not shown Access Denied
  if (!isRouteGuardPassed) {
    return <Route {...rest} render={() => <Redirect to={{ pathname: AppConstants.ROUTES.LOGIN }} />} />;
  }

  // Early return for access denied scenarios (after all hooks)
  const shouldDenyAccess = (uacKey && !hasUACAccess) || !hasFeatureAccess;

  if (shouldDenyAccess) {
    return <Route {...rest} render={() => <AccessDenied />} />;
  }

  return <Route {...rest} render={renderRoute} />;
};

const UnauthenticatedRoute = (props: GuardedRouteProps) => {
  const { component: Component, routeGuards, extraProps, isCHDManager, role, uacPermissions = [], defaultMenu, ...rest } = props;
  
  // Get default route based on defaultMenu or UAC permissions
  const defaultRoute = checkAuthRedirection(role, uacPermissions, defaultMenu);
  
  return (
    <Route
      {...rest}
      render={(props) =>
        RouteGuards(routeGuards) === false ? (
          Component ? (
            <Component {...{ ...props, ...extraProps }} />
          ) : (
            <Redirect to={{ pathname: AppConstants.ROUTES.LOGIN }} />
          )
        ) : (
          <Redirect to={{ pathname: defaultRoute }} />
        )
      }
    />
  );
};

const Routes = (props: RoutesProps) => {
  const {
    isLoggedIn,
    role,
    countryCode,
    features,
    uacPermissions = [],
    defaultMenu,
  } = props;

  // Use defaultMenu from UAC data for initial redirection
  const defaultRedirect = React.useMemo(() => {
    if (!isLoggedIn) return AppConstants.ROUTES.LOGIN;
    
    // First priority: Use defaultMenu if provided
    if (defaultMenu && UAC_ROUTE_MAP[defaultMenu]) {
      return UAC_ROUTE_MAP[defaultMenu];
    }
    
    // Second priority: Find first authorized route from UAC permissions
    for (const uacItem of uacPermissions) {
      if (uacItem.permissions.includes('VIEW') && UAC_ROUTE_MAP[uacItem.key]) {
        return UAC_ROUTE_MAP[uacItem.key];
      }
    }
    
    // Fallback to HOME if no permissions found
    return AppConstants.ROUTES.HOME;
  }, [isLoggedIn, uacPermissions, defaultMenu]);

  return (
    <Switch>
      <Redirect
        to={defaultRedirect}
        path={AppConstants.ROUTES.INDEX}
        exact
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.PLANNING}
        component={RoutePlanning}
        role={role}
        uacKey={UACConstants.SD_PLANNING}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.EXPRESS}
        component={RoutePlanning}
        role={role}
        uacKey={UACConstants.SD_PLANNING}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.CREATE_ROUTE}
        component={CreateRoute}
        role={role}
        uacKey={UACConstants.SD_ROUTES}
        uacPermission="EDIT"
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={`${AppConstants.ROUTES.VIEW_ROUTE}/:id`}
        component={CreateRoute}
        extraProps={{
          viewMode: true,
        }}
        role={role}
        uacKey={UACConstants.SD_ROUTES}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={`${AppConstants.ROUTES.EDIT_ROUTE}/:id`}
        component={CreateRoute}
        extraProps={{
          editMode: true,
        }}
        role={role}
        uacKey={UACConstants.SD_ROUTES}
        uacPermission="EDIT"
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.CARREFOUR_HOME_DLVRY}
        component={CarrefourDelivery}
        role={role}
        uacKey={UACConstants.HOME_DELIVERY}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.CLICKNCOLLECT}
        component={ClickAndCollect}
        role={role}
        uacKey={UACConstants.OD_CNC}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.PERFORMANCE}
        component={PerformanceDashboard}
        role={role}
        uacKey={UACConstants.SD_DASHBOARD}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.OPS_DASHBOARD}
        component={OpsDashboard}
        role={role}
        uacKey={UACConstants.OPS_DASHBOARD}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.APP_DOWNLOAD}
        component={QRCard}
        role={role}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.RECONCILIATION}
        component={ReconciliationDashboard}
        role={role}
        uacKey={UACConstants.SD_DASHBOARD}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.HUBWISE}
        component={HubWiseDashboard}
        role={role}
        uacKey={UACConstants.SD_DASHBOARD}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.VALETTROLLEY}
        component={ValetTrolley}
        role={role}
        uacKey={UACConstants.VALLEY_TROLLEY}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={[AppConstants.ROUTES.TRACKING, AppConstants.ROUTES.VT_CONFIG.Routes.Tracking]}
        component={RouteTracking}
        role={role}
        uacKey={UACConstants.SD_TRACKING}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={[AppConstants.ROUTES.VT_CONFIG.Routes.Hubs]}
        component={Hubs}
        role={role}
        uacKey={UACConstants.STORES}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.USERS}
        component={Users}
        role={role}
        uacKey={UACConstants.USERS}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.DELVE_DASHBOARD}
        component={DelveDashboard}
        role={role}
        uacKey={UACConstants.DELVE}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.DELVE_ORDER_LIST}
        component={DelveOrderList}
        role={role}
        uacKey={UACConstants.DELVE}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.COD_PORTAL}
        component={CODPortal}
        role={role}
        uacKey={UACConstants.ED_COD}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.STD_COD_PORTAL}
        component={StdCodPortal}
        role={role}
        uacKey={UACConstants.SD_COD}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.ALL_ORDERS}
        component={OrderList}
        role={role}
        uacKey={UACConstants.ALL_ORDERS}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.RETURNS}
        component={ReturnsList}
        role={role}
        uacKey={UACConstants.AO_RETURNS}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.CARREFOUR_NOW}
        component={Express}
        role={role}
        uacKey={UACConstants.ED_HOME}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.DRIVERS}
        component={Drivers}
        role={role}
        uacKey={UACConstants.DM_DRIVERS}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <UnauthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.RESET_PWD}
        component={UserLogin}
        extraProps={{
          resetPwd: true,
        }}
        role={role}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <UnauthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.LOGIN}
        component={UserLogin}
        role={role}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.STAGING_AREA}
        component={StagingArea}
        role={role}
        uacKey={UACConstants.STG_LOCATIONS}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.CUSTOMER_COMMUNICATION}
        component={CustomerCommunication}
        role={role}
        uacKey={UACConstants.COMMUNICATIONS}
        uacPermissions={uacPermissions}
        featuredCountry={customerCommunicationFeatureEnabledForCountries}
        countryCode={countryCode}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.APP_DOWNLOAD}
        component={DownloadApp}
        role={role}
        uacKey={UACConstants.DOWNLOAD_APP}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.REPORTS}
        component={Reports}
        role={role}
        uacKey={UACConstants.REPORT}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{ auth: isLoggedIn }}
        path={AppConstants.ROUTES.STORE_MANAGEMENT}
        component={StoreManagementPage}
        role={role}
        uacKey={UACConstants.STORES}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.DRIVER_TIPS}
        component={DriverHelperTip}
        role={role}
        uacKey={UACConstants.DM_TIPS}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      {features.includes(FeaturesConstants.DRIVER_LOCATION_DASHBOARD) && (
        <AuthenticatedRoute
          routeGuards={{
            auth: isLoggedIn,
          }}
          path={AppConstants.ROUTES.DRIVER_LOCATIONS}
          component={DriverLocations}
          role={role}
          uacPermissions={uacPermissions}
          uacKey={UACConstants.DM_LOCATIONS}
          defaultMenu={defaultMenu}
        />
      )}
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.DRIVER_ONBOARDING}
        component={OnBoardingPage}
        role={role}
        uacKey={UACConstants.DM_ONBOARDING}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        path={AppConstants.ROUTES.ADMINISTRATION}
        component={NotFound}
        role={role}
        uacKey={UACConstants.ADMINISTRATION}
        uacPermissions={uacPermissions}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        component={AccessDenied}
        role={role}
        defaultMenu={defaultMenu}
      />
      <AuthenticatedRoute
        routeGuards={{
          auth: isLoggedIn,
        }}
        component={NotFound}
        role={role}
        defaultMenu={defaultMenu}
      />
    </Switch>
  );
};

export default Routes;
